import {
  ExpandableNavigationVariant,
  NavigationItemAlignment,
  NavigationItemDirection,
  NavigationPosition,
  NavigationResolution,
} from '@unifyapps/defs/types/interface';
import { Box } from '@unifyapps/ui/components/Box';
import type { TooltipModes } from '@unifyapps/ui/components/Tooltip';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { Typography } from '@unifyapps/ui/components/Typography';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import { Link } from '../../../navigation';
import { DATA_NAV_ITEM_ATTRIBUTE, NavigationItemClassName } from '../constants';
import { isHorizontalUpfrontFn, isPlatformSideNav } from '../helpers';
import {
  navIconContainerVariants,
  navItemContainerVariants,
  navItemIconRootClassName,
  navItemLinkIconVariants,
  navItemLinkLabelVariants,
  navItemLinkRootVariants,
} from '../styles';
import type { NavItemNavigationOptions, NavigationItemSlotPropsType } from '../types';
import { NavigationItemLabelVariantEnum, NavigationItemSelectionVariantEnum } from '../types';
import { getRedirectionProps, resolveOpenInNewTab } from '../utils/redirection';
import { NavigationIcon } from './NavigationIcon';

interface NavItemProps {
  // id of the underlying nav item. Optional: some callers (LiteSideNavigation, the theme
  // builder's tab layout) render this component for rows that have no nav item id.
  id?: string;
  isActive?: boolean;
  icon?: string;
  activeIcon?: string;
  endIcon?: string;
  activeEndIcon?: string;
  name: string;
  url: string;
  navigationOptions?: NavItemNavigationOptions;
  /** @deprecated Use `navigationOptions.openInNewTab` instead. */
  openInNewTab?: boolean;
  variant: 'lite' | 'rich';
  slotProps?: NavigationItemSlotPropsType;
  renderEndDecorator?: () => React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

export default function NavigationItem(props: NavItemProps) {
  const {
    id,
    isActive = false,
    icon,
    activeIcon,
    endIcon,
    activeEndIcon,
    name,
    url,
    variant,
    navigationOptions,
    openInNewTab,
    slotProps,
    renderEndDecorator,
    onClick,
    disabled,
  } = props;

  const { navigationItem, container } = slotProps ?? {};

  const {
    position = NavigationPosition.Start,
    expanded = true,
    labelVariant = NavigationItemLabelVariantEnum.Expandable,
    resolution = NavigationResolution.XL,
    selectionVariant = NavigationItemSelectionVariantEnum.Stroke,
    expandableNavVariant = ExpandableNavigationVariant.SM,
    direction = NavigationItemDirection.Column,
    alignment = NavigationItemAlignment.Start,
    typographyClassName,
    icon: iconProps,
    navItemClassName,
    navItemStyles,
    navItemBorderRadiusClassName,
    navItemBorderRadiusStyles,
    hideIcon,
  } = navigationItem ?? {};

  const _isPlatformSideNav = isPlatformSideNav(labelVariant);

  const size = iconProps?.size;

  const rootClassname = navItemLinkRootVariants({
    active: isActive,
    variant,
    className: clsx(navItemIconRootClassName({ hasIcon: Boolean(icon) }), {
      'h-fit px-lg py-md rounded-full': position === NavigationPosition.Top,
    }),
    labelVariant,
    selectionVariant,
    position,
    resolution,
    expandableNavVariant,
    expanded,
    direction,
    alignment,
  });

  const labelClassname = navItemLinkLabelVariants({
    active: isActive,
    expanded,
    variant,
    labelVariant,
    selectionVariant,
    position,
    resolution,
    disabled,
    isPlatformSideNav: _isPlatformSideNav,
  });

  const isHorizontalUpfront = isHorizontalUpfrontFn(labelVariant, selectionVariant, direction);

  const iconClassname = navItemLinkIconVariants({
    className: 'shrink-0',
    active: isActive,
    variant,
    resolution,
    labelVariant,
    selectionVariant,
    size,
    isHorizontalUpfront,
    disabled,
  });

  const containerClassName = navItemContainerVariants({
    className: clsx(
      NavigationItemClassName[position],
      navItemBorderRadiusClassName,
      resolution === NavigationResolution.XL && !disabled ? 'cursor-pointer' : '',
      { 'items-center justify-center': position === NavigationPosition.Top },
      { '!pointer-events-none': disabled },
    ),
    variant,
    position,
    selectionVariant,
    labelVariant,
    direction,
    alignment,
    active: isActive,
    isHorizontalUpfront,
  });

  const navIconContainerClassName = navIconContainerVariants({
    labelVariant,
    position,
    selectionVariant,
    variant,
    active: isActive,
    resolution,
    direction,
    alignment,
    isHorizontalUpfront,
  });

  const strokeIconClassName = isActive
    ? 'ua-side-nav-item-active'
    : 'ua-side-nav-item-active-false';
  const solidIconClassName = isActive
    ? 'ua-side-nav-item-active-solid'
    : 'ua-side-nav-item-active-false-solid';

  const _iconClassName =
    selectionVariant === NavigationItemSelectionVariantEnum.Solid
      ? solidIconClassName
      : strokeIconClassName;

  const openInNewTabResolved = resolveOpenInNewTab({
    navigationOptions,
    openInNewTab,
  });
  const redirectionProps = useMemo(
    () => getRedirectionProps(openInNewTabResolved),
    [openInNewTabResolved],
  );

  const rootElContent = (
    <>
      {icon ? (
        <Box className={navIconContainerClassName}>
          <Box
            className={clsx({
              hidden: activeIcon && isActive,
            })}
          >
            <NavigationIcon
              className={iconClassname}
              disabled={disabled}
              hideIcon={hideIcon}
              iconClassName={_iconClassName}
              iconName={icon}
              isActive={isActive}
              isHorizontalUpfront={isHorizontalUpfront}
              label={name}
              selectionVariant={selectionVariant}
              size={size}
            />
          </Box>
          {activeIcon ? (
            <Box
              className={clsx({
                hidden: !isActive,
              })}
            >
              <NavigationIcon
                className={iconClassname}
                disabled={disabled}
                hideIcon={hideIcon}
                iconClassName={_iconClassName}
                iconName={activeIcon}
                isActive={isActive}
                isHorizontalUpfront={isHorizontalUpfront}
                label={name}
                selectionVariant={selectionVariant}
                size={size}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
      {expanded || labelVariant === NavigationItemLabelVariantEnum.Upfront ? (
        <Typography className={slowCn(labelClassname, typographyClassName)} maxNumberOfLines={1}>
          {name}
        </Typography>
      ) : null}
      {endIcon && expanded ? (
        <Box className={clsx('ms-auto', navIconContainerClassName)}>
          <Box
            className={clsx({
              hidden: activeEndIcon && isActive,
            })}
          >
            <NavigationIcon
              className={iconClassname}
              disabled={disabled}
              hideIcon={hideIcon}
              iconClassName={_iconClassName}
              iconName={endIcon}
              isActive={isActive}
              isHorizontalUpfront={isHorizontalUpfront}
              label={name}
              selectionVariant={selectionVariant}
              size={size}
            />
          </Box>
          {activeEndIcon ? (
            <Box
              className={clsx({
                hidden: !isActive,
              })}
            >
              <NavigationIcon
                className={iconClassname}
                disabled={disabled}
                hideIcon={hideIcon}
                iconClassName={_iconClassName}
                iconName={activeEndIcon}
                isActive={isActive}
                isHorizontalUpfront={isHorizontalUpfront}
                label={name}
                selectionVariant={selectionVariant}
                size={size}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
      {renderEndDecorator?.()}
    </>
  );

  const el = url ? (
    <Link
      className={slowCn(rootClassname, navItemClassName)}
      href={url}
      onClick={onClick}
      style={navItemStyles}
      {...{ [DATA_NAV_ITEM_ATTRIBUTE]: id }}
      {...redirectionProps}
    >
      {rootElContent}
    </Link>
  ) : (
    <Box
      className={slowCn(rootClassname, navItemClassName)}
      style={navItemStyles}
      {...{ [DATA_NAV_ITEM_ATTRIBUTE]: id }}
    >
      {rootElContent}
    </Box>
  );

  return (
    <Box
      className={slowCn(containerClassName, container?.className)}
      data-label-variant={labelVariant}
      style={navItemBorderRadiusStyles}
    >
      {labelVariant === NavigationItemLabelVariantEnum.Tooltip ? (
        <Tooltip
          arrow={false}
          mode={slotProps?.theme as unknown as TooltipModes}
          offset={6}
          placement="right"
          title={name}
        >
          {el}
        </Tooltip>
      ) : (
        el
      )}
    </Box>
  );
}
